import { useCallback, useEffect, useState } from "react";
import { Tooltip, useToast } from "@mieweb/ui";
import { ArrowDownToLine, RefreshCw, Sparkles } from "lucide-react";
import { useAppUpdate } from "../hooks/useAppUpdate";
import { WhatsNewModal } from "./WhatsNewModal";
import {
  changelog,
  CHANGELOG_STORAGE_KEY,
  LATEST_CHANGELOG_VERSION,
} from "../data/changelog";
import { compareVersions } from "../lib/build-info";
import { checkForDesktopUpdate } from "../lib/desktop";

/**
 * The sidebar update badge. Invisible until something's worth saying:
 *
 *  - a deploy is rolling out ("Updating…"),
 *  - a newer web build is live ("Update available" → What's New + reload), or
 *  - a desktop update is ready ("Update available" → download + install).
 *
 * It also auto-opens the What's New reader once per release, so right after
 * the app reloads into a new version the user gets a friendly summary of
 * what changed.
 */
export function UpdateBadge({ collapsed = false }: { collapsed?: boolean }) {
  const state = useAppUpdate();
  const { error: toastError } = useToast();
  const [modalOpen, setModalOpen] = useState(false);
  const [installing, setInstalling] = useState(false);

  // Auto-open the What's New reader the first time we run a newer release.
  useEffect(() => {
    if (changelog.length === 0) return;
    let seen: string | null = null;
    try {
      seen = localStorage.getItem(CHANGELOG_STORAGE_KEY);
    } catch {
      /* storage blocked — treat as unseen */
    }
    if (seen && compareVersions(LATEST_CHANGELOG_VERSION, seen) > 0) {
      setModalOpen(true);
    } else if (!seen) {
      // Brand-new device — don't spring the modal, just record the version.
      try {
        localStorage.setItem(CHANGELOG_STORAGE_KEY, LATEST_CHANGELOG_VERSION);
      } catch {
        /* ignore */
      }
    }
  }, []);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    try {
      localStorage.setItem(CHANGELOG_STORAGE_KEY, LATEST_CHANGELOG_VERSION);
    } catch {
      /* ignore */
    }
  }, []);

  const installDesktop = useCallback(async () => {
    setInstalling(true);
    try {
      const result = await checkForDesktopUpdate({ interactive: true });
      if (result.status === "error") {
        toastError("Could not install the update. Try again later.");
      }
      // 'installing' relaunches the app; other outcomes need no toast.
    } finally {
      setInstalling(false);
    }
  }, [toastError]);

  if (state.kind === "none") {
    return <WhatsNewModal open={modalOpen} onClose={closeModal} />;
  }

  const container = `shrink-0 border-t border-border ${
    collapsed ? "flex justify-center py-2" : "px-3 py-2"
  }`;

  if (state.kind === "deploying") {
    const button = (
      <button
        type="button"
        onClick={() => setModalOpen(true)}
        aria-label="An update is rolling out"
        className={
          collapsed
            ? "flex h-10 w-10 items-center justify-center rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
            : "flex w-full items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 px-3 py-2 text-sm text-amber-700 dark:text-amber-300"
        }
      >
        <RefreshCw className="h-4 w-4 shrink-0 animate-spin" />
        {!collapsed && <span className="font-medium">Updating…</span>}
      </button>
    );
    return (
      <>
        <div className={container}>
          {collapsed ? (
            <Tooltip content="An update is rolling out" placement="right">
              {button}
            </Tooltip>
          ) : (
            button
          )}
        </div>
        <WhatsNewModal open={modalOpen} onClose={closeModal} />
      </>
    );
  }

  const isDesktopUpdate = state.kind === "desktop-ready";
  const newVersion = state.version;
  const label = installing ? "Updating…" : "Update available";
  const Icon = isDesktopUpdate ? ArrowDownToLine : Sparkles;

  const button = (
    <button
      type="button"
      disabled={installing}
      onClick={() => (isDesktopUpdate ? void installDesktop() : setModalOpen(true))}
      aria-label={`Update available${newVersion ? ` (v${newVersion})` : ""}`}
      className={
        (collapsed
          ? "flex h-10 w-10 items-center justify-center rounded-lg text-primary hover:bg-primary/10"
          : "flex w-full items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary hover:bg-primary/20 transition-colors") +
        " disabled:opacity-50 disabled:cursor-not-allowed"
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span className="font-medium">{label}</span>}
    </button>
  );

  return (
    <>
      <div className={container}>
        {collapsed ? (
          <Tooltip
            content={`Update available${newVersion ? ` (v${newVersion})` : ""}`}
            placement="right"
          >
            {button}
          </Tooltip>
        ) : (
          button
        )}
      </div>
      <WhatsNewModal
        open={modalOpen}
        onClose={closeModal}
        onReload={
          state.kind === "web-ready" ? () => window.location.reload() : undefined
        }
      />
    </>
  );
}
